import { Button, Space, notification } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Hand } from '../components/Hand';
import { PageHead } from '../components/PageHead';
import { SlideController } from '../components/SlideController';
import { api, type EventSpeakerRow } from '../lib/api';
import { useAuth } from '../lib/auth';
import { getPlatformString, isWindowVisible } from '../lib/clientInfo';
import { wsUrl } from '../lib/config';
import { showTeamMessage } from '../lib/teamMessage';
import { useWebSocket } from '../lib/ws';

type IncomingMessage =
  | IncomingGoLive
  | { type: 'go-waiting' }
  | { type: 'event-status'; status: 'draft' | 'live' | 'ended' }
  | { type: 'refresh'; speakerId: string }
  | { type: 'hand'; speakerId: string; raised: boolean; loweredBy: 'team' | 'speaker' | null }
  | {
      type: 'message';
      speakerId?: string;
      text: string;
      level: 'info' | 'warning' | 'urgent';
      sentAt: number;
    };

type Self = EventSpeakerRow & {
  name: string;
  email: string;
  resolved_slide_key: string | null;
  slot_source_type: 'vmix_call' | 'zoom' | null;
  slot_vmix_id: string | null;
  slot_zoom_meeting_id: string | null;
  slot_zoom_passcode: string | null;
};

type IncomingGoLive =
  | { type: 'go-live'; sourceType: 'vmix_call'; vmixId: string }
  | {
      type: 'go-live';
      sourceType: 'zoom';
      zoomMeetingId: string;
      zoomPasscode: string | null;
    }
  // Legacy payload: pre-Pass-L assigns omitted sourceType.
  | { type: 'go-live'; vmixId: string };

export function StageScene() {
  const { eventId } = useParams<{ eventId: string }>();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { session } = useAuth();
  const [vmix, setVmix] = useState<string | null>(params.get('vmix'));
  const [zoom, setZoom] = useState<{ meetingId: string; passcode: string | null } | null>(null);
  const [self, setSelf] = useState<Self | null>(null);
  const [handRaised, setHandRaised] = useState(false);

  useEffect(() => {
    const v = params.get('vmix');
    if (v) setVmix(v);
  }, [params]);

  useEffect(() => {
    if (!eventId) return;
    void (async () => {
      try {
        const data = await api<Self>(`/api/event/${eventId}/me`);
        setSelf(data);
        if (data.slot_source_type === 'zoom' && data.slot_zoom_meeting_id) {
          setZoom({
            meetingId: data.slot_zoom_meeting_id,
            passcode: data.slot_zoom_passcode,
          });
          setVmix(null);
        } else if (data.slot_source_type === 'vmix_call' && data.slot_vmix_id) {
          setVmix(data.slot_vmix_id);
        }
      } catch {
        // not critical for the iframe — slide control just won't render
      }
    })();
  }, [eventId]);

  const url = useMemo(() => {
    if (!eventId || !session) return null;
    return wsUrl(
      `/api/event/${eventId}/waiting?role=speaker&token=${encodeURIComponent(session.token)}`,
    );
  }, [eventId, session]);

  const { send } = useWebSocket<IncomingMessage>(
    url,
    (msg) => {
      if (msg.type === 'go-waiting' && eventId) {
        navigate(`/waiting/${eventId}`);
      } else if (msg.type === 'go-live') {
        if ('sourceType' in msg && msg.sourceType === 'zoom') {
          setZoom({ meetingId: msg.zoomMeetingId, passcode: msg.zoomPasscode });
          setVmix(null);
        } else {
          setVmix('vmixId' in msg ? msg.vmixId : null);
          setZoom(null);
        }
      } else if (msg.type === 'event-status' && msg.status === 'ended' && eventId) {
        navigate(`/waiting/${eventId}`);
      } else if (msg.type === 'refresh') {
        window.location.reload();
      } else if (msg.type === 'hand') {
        setHandRaised(msg.raised);
        if (!msg.raised && msg.loweredBy === 'team') {
          notification.info({
            message: 'Your hand was lowered by the team.',
            placement: 'topRight',
          });
        }
      } else if (msg.type === 'message') {
        showTeamMessage(msg.text, msg.level);
      }
    },
    {
      onOpen: (sendImmediately) => {
        sendImmediately({
          type: 'client-info',
          platform: getPlatformString(),
          visible: isWindowVisible(),
          location: window.location.pathname,
        });
      },
    },
  );

  useEffect(() => {
    const onVisibility = () => send({ type: 'client-info', visible: isWindowVisible() });
    const onFocus = () => send({ type: 'client-info', visible: true });
    const onBlur = () => send({ type: 'client-info', visible: false });
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', onFocus);
    window.addEventListener('blur', onBlur);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('blur', onBlur);
    };
  }, [send]);

  if (!vmix && !zoom) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <PageHead title="Stage" />
        <p>Connecting…</p>
      </div>
    );
  }

  const name = self?.name ?? session?.email ?? '';
  const src = vmix
    ? `https://advanced.vmixcall.com/call.htm?Key=${encodeURIComponent(vmix)}&Name=${encodeURIComponent(name)}`
    : null;
  const zoomJoinUrl = zoom
    ? `https://app.zoom.us/wc/join/${encodeURIComponent(
        zoom.meetingId.replace(/\s+/g, ''),
      )}${zoom.passcode ? `?pwd=${encodeURIComponent(zoom.passcode)}` : ''}`
    : null;
  // Prefer the license-resolved key when present; fall back to the legacy
  // per-speaker key so older event_speakers keep working without a fresh
  // assignment.
  const slideKey =
    self?.resolved_slide_key ??
    (self?.slide_control_enabled ? self?.slide_control_key ?? null : null);

  const toggleHand = async () => {
    if (!eventId) return;
    const next = !handRaised;
    setHandRaised(next);
    try {
      await api(`/api/event/${eventId}/hand`, {
        method: 'POST',
        body: JSON.stringify({ raised: next }),
      });
    } catch {
      setHandRaised(!next);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', background: '#fff' }}>
      <PageHead title="Stage" />
      {slideKey && <SlideController slideKey={slideKey} />}
      {src && (
        <iframe
          title="AOTV vMix"
          src={src}
          allow="camera; microphone; fullscreen; autoplay; display-capture"
          allowFullScreen
          style={{ border: 0, width: '100%', flex: 1, background: '#fff' }}
        />
      )}
      {zoomJoinUrl && (
        <div
          style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 24,
            background: '#fafbfc',
          }}
        >
          <div
            style={{
              maxWidth: 480,
              width: '100%',
              padding: '32px 24px',
              border: '1px solid #e5eaf0',
              borderRadius: 12,
              textAlign: 'center',
              background: '#fff',
            }}
          >
            <h2 style={{ marginTop: 0 }}>You're joining via Zoom</h2>
            <p style={{ color: '#666' }}>
              Click below to open the Zoom meeting. Your video and audio go
              into the broadcast through Zoom — keep this page open so the
              team can still message you.
            </p>
            <div
              style={{
                background: '#fafbfc',
                border: '1px solid #e5eaf0',
                borderRadius: 6,
                padding: '12px 16px',
                margin: '24px 0',
                textAlign: 'left',
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                fontSize: 14,
              }}
            >
              <div>
                <strong>Meeting ID:</strong> {zoom?.meetingId}
              </div>
              {zoom?.passcode && (
                <div>
                  <strong>Passcode:</strong> {zoom.passcode}
                </div>
              )}
            </div>
            <a
              href={zoomJoinUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'inline-block',
                padding: '12px 24px',
                background: '#428e3e',
                color: '#fff',
                borderRadius: 6,
                fontWeight: 600,
                textDecoration: 'none',
              }}
            >
              Join Zoom Meeting
            </a>
          </div>
        </div>
      )}
      <Button
        type="text"
        size="small"
        onClick={() => navigate('/me')}
        style={{
          position: 'fixed',
          top: 8,
          left: 8,
          background: 'rgba(255,255,255,0.85)',
          zIndex: 1000,
        }}
      >
        ← My events
      </Button>
      <div
        style={{
          position: 'fixed',
          right: 16,
          bottom: 16,
          zIndex: 1000,
          pointerEvents: 'auto',
        }}
      >
        {handRaised ? (
          <Space>
            <span
              style={{
                background: '#fff',
                border: '1px solid #e5eaf0',
                borderRadius: 999,
                padding: '4px 12px',
                fontSize: 22,
              }}
            >
              <Hand size={22} />
            </span>
            <Button shape="round" size="large" onClick={toggleHand}>
              Lower Hand
            </Button>
          </Space>
        ) : (
          <Button
            shape="round"
            size="large"
            type="primary"
            onClick={toggleHand}
            style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}
          >
            ✋ Raise Hand
          </Button>
        )}
      </div>
    </div>
  );
}
